import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { StateValues } from 'react-use-form-state';
import { AdminApi, AdminProductDistributionPlatformListing, DistributionPlatform, DocumentsApi } from 'api';
import useApiCall from 'hooks/use-api-call';
import Translate from 'ui/atoms/translate';
import DistributionInformationModal, { DistributionInformationModalFields } from '../distribution-information-modal';
import { AdminDistributorProductDistributionInformationRequest } from 'src/api/models/AdminDistributorProductDistributionInformationRequest';

interface AddDistributionInformationProps {
  onDistributionInformationAdded: () => void;
  onClose: () => void;
  listings: AdminProductDistributionPlatformListing[];
  distributionPlatformsWithEditPermission: DistributionPlatform[];
}

const AddDistributionInformation: FunctionComponent<AddDistributionInformationProps> = ({
  onDistributionInformationAdded,
  onClose,
  listings,
  distributionPlatformsWithEditPermission,
}) => {
  const [platformsWithNoDistributionInformation, setPlatformsWithNoDistributionInformation] = useState<
    AdminProductDistributionPlatformListing[]
  >([]);

  const { makeAuthenticatedApi, withApi, loading: apiLoading, error } = useApiCall();

  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);
  const documentsApi: DocumentsApi = useMemo(() => makeAuthenticatedApi(DocumentsApi), [makeAuthenticatedApi]);

  const onSubmit = useCallback(
    (fieldsValues: StateValues<DistributionInformationModalFields>, productStartDate: Date, productEndDate: Date) => {
      withApi(async () => {
        const params: AdminDistributorProductDistributionInformationRequest = {
          complianceProductName: fieldsValues.complianceProductName,
          useUnits: fieldsValues.useUnits === 'true',
          selfDisclosureRequired: fieldsValues.selfDisclosureRequired,
          investmentExperienceMinScore: Number(fieldsValues.investmentExperienceMinScore),
          serviceExperienceMinScore: Number(fieldsValues.serviceExperienceMinScore),
          costsvariables: {
            capitalType: fieldsValues.capitalType,
            costsBrokerageNominalAmountPercent: fieldsValues.costsBrokerageNominalAmountPercent,
            grantsNominalAmountPercent: fieldsValues.grantsNominalAmountPercent,
            costsBrokerageNavPercent: fieldsValues.costsBrokerageNavPercent,
            grantsNavPercent: fieldsValues.grantsNavPercent,
            costsTransactionDepotNominalAmountPercent: fieldsValues.costsTransactionDepotNominalAmountPercent,
            costsTransactionDepotNavPercent: fieldsValues.costsTransactionDepotNavPercent,
          },
        };

        if (fieldsValues.unitSteps) {
          params.unitSteps = fieldsValues.unitSteps;
        }
        if (fieldsValues.runtimeYears) {
          params.costsvariables.runtimeYears = Number(fieldsValues.runtimeYears);
        } else {
          params.costsvariables.productStartDate = productStartDate;
          params.costsvariables.productEndDate = productEndDate;
        }

        if (fieldsValues.productImage?.file) {
          const docResponse = await documentsApi.adminDocumentsCreate({
            file: fieldsValues.productImage.file,
          });
          params.productImageId = docResponse.id;
        }

        await api.adminListingsDistributionInformationCreate({
          id: fieldsValues.distributionPlatform,
          adminDistributorProductDistributionInformationRequest: params,
        });
        onDistributionInformationAdded();
      });
    },
    [api, documentsApi],
  );

  useEffect(() => {
    setPlatformsWithNoDistributionInformation(
      listings.filter((listing) =>
        distributionPlatformsWithEditPermission.some((platform) => platform.id === listing.distributionPlatformId),
      ),
    );
  }, [JSON.stringify(listings), JSON.stringify(distributionPlatformsWithEditPermission)]);

  return (
    <DistributionInformationModal
      platformsWithNoDistributionInformation={platformsWithNoDistributionInformation}
      onSubmit={onSubmit}
      onClose={onClose}
      modalHeader={<Translate name="distributionInformation.add" />}
      loading={apiLoading}
      error={error}
    ></DistributionInformationModal>
  );
};

export default AddDistributionInformation;
